import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { uploadWhatsAppMediaAsync } from '../helpers/apiHandler';
import { applyTextFormat } from '../helpers/textFormatHelper';

const useWhatsAppTemplate = (initialTemplate = {}) => {
  const { access_token } = useSelector((state) => state.auth.user);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [template, setTemplate] = useState({
    name: '',
    language: '',
    category: 'MARKETING',
    headerType: 'TEXT', // Default header type
    headerMediaHandle: null, // For storing media asset handle
    headerMediaPreviewUrl: null, // For local preview
    components: [],
    ...initialTemplate,
  });

  const [templateHeader, setTemplateHeader] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [templateFooter, setTemplateFooter] = useState('');
  const [templateButtons, setTemplateButtons] = useState([]);
  const [bodyVariables, setBodyVariables] = useState([]);
  const [bodyVariableEntries, setBodyVariableEntries] = useState([]);
  const [codeExp, setCodeExp] = useState(5);
  const [validityPeriod, setValidityPeriod] = useState(10); // Default validity period is 10 minutes
  const [securityRecommendation, setSecurityRecommendation] = useState(true); // Default to true for security recommendation
  const [showExpiryTime, setShowExpiryTime] = useState(true); // Default to true for showing expiry time

  const bodyTextareaRef = useRef(null);


  const handleTemplateChange = useCallback((field, value) => {
    setTemplate((prev) => ({
      ...prev,
      [field]: value,
    }));

    // When category changes to AUTHENTICATION, set up the OTP button
    if (field === 'category' && value === 'AUTHENTICATION') {
      // Initialize a default OTP button
      // Note: The 'text' field is only used for preview and won't be sent to WhatsApp API
      setTemplateButtons([{
        type: 'OTP',
        text: 'Copy code', // For preview only
        otp_type: 'COPY_CODE'
      }]);

      // Authentication templates have a fixed format: 
      // "{{1}} is your verification code. For your security, do not share this code."
      const authBody = '{{1}}';
      setTemplateBody(authBody);
      setBodyVariables(['{{1}}']);
      // Add a default example value
      setBodyVariableEntries(['123456']);

      // Set security recommendation to true by default
      setSecurityRecommendation(true);
    }
  }, [templateBody]);

  const handleBodyChange = useCallback((newBody) => {
    setTemplateBody(newBody);
    // Extract variables using regex and set them directly
    const variablePattern = /\{\{(\d+)\}\}/g;
    const matches = [];
    let match;
    while ((match = variablePattern.exec(newBody)) !== null) {
      matches.push(`{{${match[1]}}}`);
    }
    // Sort variables by their number to ensure correct order
    matches.sort((a, b) => {
      const numA = parseInt(a.match(/\d+/)[0]);
      const numB = parseInt(b.match(/\d+/)[0]);
      return numA - numB;
    });
    setBodyVariables(matches);
  }, []);

  const handleAddVariable = useCallback(() => {
    // For authentication templates, only allow one variable
    if (template.category === 'AUTHENTICATION' && bodyVariables.length >= 1) {
      console.warn('[useWhatsAppTemplate] Authentication templates can only have one variable');
      return;
    }

    const newVariableNumber = bodyVariables.length + 1;
    const newVariable = `{{${newVariableNumber}}}`;

    // Update body text with the new variable
    const updatedBody = `${templateBody}${newVariable}`;
    setTemplateBody(updatedBody);

    // Update variables array directly
    setBodyVariables((prev) => [...prev, newVariable]);

    // Focus the textarea and place cursor after the new variable
    if (bodyTextareaRef.current) {
      setTimeout(() => {
        bodyTextareaRef.current.focus();
        bodyTextareaRef.current.setSelectionRange(updatedBody.length, updatedBody.length);
      }, 0);
    }
  }, [bodyVariables, templateBody, template.category]);

  const handleRemoveVariable = useCallback(
    (index) => {
      // Get the variable to remove
      const variableToRemove = bodyVariables[index];

      // Remove the variable from the body text
      const updatedBody = templateBody.replace(variableToRemove, '');
      setTemplateBody(updatedBody);

      // Remove the variable from the variables array
      setBodyVariables((prev) => prev.filter((_, i) => i !== index));

      // Remove the corresponding entry
      setBodyVariableEntries((prev) => {
        const newEntries = [...prev];
        newEntries.splice(index, 1);
        return newEntries;
      });
    },
    [bodyVariables, templateBody]
  );

  const handleVariableEntryChange = useCallback((index, value) => {
    setBodyVariableEntries((prev) => {
      const newEntries = [...prev];
      newEntries[index] = value;
      return newEntries;
    });
  }, []);

  const handleButtonChange = useCallback((index, fieldOrFields, valueOrNull) => {
    setTemplateButtons((prev) => {
      const newButtons = [...prev];

      // Check if we're updating multiple fields at once (object passed)
      if (typeof fieldOrFields === 'object' && fieldOrFields !== null) {
        newButtons[index] = {
          ...newButtons[index],
          ...fieldOrFields // Spread the fields object to update multiple properties
        };
      } else {
        // Original behavior - update a single field
        newButtons[index] = {
          ...newButtons[index],
          [fieldOrFields]: valueOrNull,
        };
      }

      return newButtons;
    });
  }, []);

  const handleAddButton = useCallback((buttonType = 'QUICK_REPLY') => {
    if (templateButtons.length < 3) {
      setTemplateButtons((prev) => {
        const newButton = {
          type: buttonType,
          text: '',
        };

        // Add specific properties based on button type
        if (buttonType === 'URL') {
          newButton.url = '';
        } else if (buttonType === 'PHONE_NUMBER') {
          newButton.phoneNumber = '';
          newButton.countryCode = '+1';
        }

        return [...prev, newButton];
      });
    }
  }, [templateButtons.length]);

  const handleRemoveButton = useCallback((index) => {
    setTemplateButtons((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleTextFormat = useCallback(
    (formatType) => {
      const textarea = bodyTextareaRef.current;
      if (!textarea) return;

      const result = applyTextFormat({
        text: templateBody,
        start: textarea.selectionStart,
        end: textarea.selectionEnd,
        formatType,
      });

      if (result) {
        setTemplateBody(result.text);
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(result.selectionStart, result.selectionEnd);
        }, 0);
      }
    },
    [templateBody]
  );

  const handleMediaUpload = useCallback(async (file) => {
    if (!file) {
      setUploadError('No file provided');
      return false;
    }

    // Validate file size based on type
    const fileType = template.headerType.toLowerCase();
    const maxSize = fileType === 'image' ? 5 * 1024 * 1024 : 16 * 1024 * 1024; // 5MB for images, 16MB for videos

    if (file.size > maxSize) {
      const sizeInMB = Math.round(maxSize / (1024 * 1024));
      setUploadError(`File too large. Maximum size for ${fileType} is ${sizeInMB}MB.`);
      return false;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Use the specialized uploadWhatsAppMediaAsync function with the correct parameters
      const result = await uploadWhatsAppMediaAsync(file, fileType, access_token.token);

      // Check if the handle exists in the response
      if (result.success && result.data && result.data.handle) {
        // Update template state with the asset handle
        handleTemplateChange('headerMediaHandle', result.data.handle);

        // Create a local preview URL for the UI
        const previewUrl = URL.createObjectURL(file);
        handleTemplateChange('headerMediaPreviewUrl', previewUrl);

        return true;
      } else {
        // Handle case where success is true but handle is missing
        const errorMsg = result.success
          ? 'Upload succeeded but no media handle was returned.'
          : (result.message || 'Upload failed');

        console.error('[useWhatsAppTemplate] Upload issue:', errorMsg);
        setUploadError(errorMsg);
        return false;
      }
    } catch (error) {
      console.error('[useWhatsAppTemplate] Upload error:', error);
      setUploadError(error.message || 'An unexpected error occurred during upload');
      return false;
    } finally {
      setIsUploading(false);
    }
  }, [template.headerType, access_token, handleTemplateChange]);

  const validateTemplate = useCallback(() => {
    // Check basic required fields
    if (!template.name || !template.language || !template.category) {
      return false;
    }

    // Validate media header if present
    if (template.headerType && template.headerType !== 'TEXT') {
      if (!template.headerMediaHandle) {
        return false;
      }
    }

    // Validate based on template category
    if (template.category === 'AUTHENTICATION') {
      // Authentication templates require:
      // 1. EXACTLY one variable in the body
      // 2. Valid code expiration (1-60 minutes)
      // 3. OTP button (if buttons are present)

      // Check for exactly one variable
      if (bodyVariables.length !== 1 || !templateBody.includes('{{1}}')) {
        console.error('[validateTemplate] Authentication templates must have exactly one variable {{1}}');
        return false;
      }

      const validCodeExp = codeExp >= 1 && codeExp <= 60;

      // Check if buttons are valid for authentication template
      const validButtons =
        templateButtons.length === 0 ||
        (templateButtons.length === 1 && templateButtons[0].type === 'OTP');

      if (!validCodeExp) {
        console.error('[validateTemplate] Invalid code expiration time');
        return false;
      }

      if (!validButtons) {
        console.error('[validateTemplate] Invalid buttons for Authentication template');
        return false;
      }

      // Check if variables have examples
      if (bodyVariables.length > 0) {
        const missingExamples =
          bodyVariables.length >
          bodyVariableEntries.filter((entry) => entry && entry.trim()).length;
        if (missingExamples) {
          console.error('[validateTemplate] Authentication templates must have examples for all variables');
          return false;
        }
      }

      return true;
    } else {
      // UTILITY and MARKETING templates

      // Check if buttons are valid (not OTP type)
      const hasInvalidButtons = templateButtons.some((button) => button.type === 'OTP');
      if (hasInvalidButtons) {
        return false;
      }

      // Check URL buttons have valid URLs
      const urlButtons = templateButtons.filter((button) => button.type === 'URL');
      const hasInvalidUrls = urlButtons.some((button) => !button.url || !button.url.trim());
      if (hasInvalidUrls) {
        return false;
      }

      // Check PHONE_NUMBER buttons have valid phone numbers
      const phoneButtons = templateButtons.filter((button) => button.type === 'PHONE_NUMBER');
      const hasInvalidPhones = phoneButtons.some(
        (button) => !button.phoneNumber || !button.phoneNumber.trim()
      );
      if (hasInvalidPhones) {
        return false;
      }

      // Check all buttons have text
      const hasEmptyButtonText = templateButtons.some(
        (button) => !button.text || !button.text.trim()
      );
      if (hasEmptyButtonText) {
        return false;
      }

      // Check if variables have examples
      if (bodyVariables.length > 0) {
        const missingExamples =
          bodyVariables.length >
          bodyVariableEntries.filter((entry) => entry && entry.trim()).length;
        if (missingExamples) {
          return false;
        }
      }

      return true;
    }
  }, [template, templateBody, templateButtons, bodyVariables, bodyVariableEntries, codeExp]);

  const getTemplatePayload = useCallback(() => {
    const payload = {
      name: template.name,
      language: template.language,
      category: template.category,
      components: [],
    };

    // Add header component if it exists
    if (templateHeader || (template.headerType && template.headerType !== 'TEXT' && template.headerMediaHandle)) {
      const headerComponent = {
        type: 'HEADER',
        format: template.headerType || 'TEXT',
      };

      // For text headers, add the text property
      if (!template.headerType || template.headerType === 'TEXT') {
        headerComponent.text = templateHeader;
      }
      // For media headers, add the example with header_handle
      else if (template.headerMediaHandle) {
        headerComponent.example = {
          header_handle: template.headerMediaHandle // Ensure this is passed as a string
        };

        // Log the structure being sent
        console.log(`[getTemplatePayload] Adding ${template.headerType} header with handle:`, template.headerMediaHandle);
      }

      payload.components.push(headerComponent);
    }

    // Add body component
    const bodyComponent = {
      type: 'BODY',
    };

    // For authentication templates, we need a different structure
    if (template.category === 'AUTHENTICATION') {
      // Authentication templates require add_security_recommendation
      bodyComponent.add_security_recommendation = securityRecommendation;

      // Make sure authentication templates always have an example field
      // This is required by the WhatsApp API
      const filteredExamples = bodyVariableEntries
        .filter((entry) => entry && entry.trim())
        .map((entry) => entry);

      if (filteredExamples.length > 0) {
        bodyComponent.example = {
          body_text: [filteredExamples],
        };
      } else {
        // If no valid examples, add a default one to prevent API errors
        bodyComponent.example = {
          body_text: [['123456']],
        };
      }

      // Authentication templates don't have a text field - it's preset by WhatsApp
    } else {
      bodyComponent.text = templateBody;

      // Add examples for variables in non-authentication templates (MARKETING, UTILITY)
      if (bodyVariables.length > 0) {
        const filteredExamples = bodyVariableEntries
          .filter((entry) => entry && entry.trim())
          .map((entry) => entry);

        // If we have variables but not enough examples entered, add defaults
        if (filteredExamples.length < bodyVariables.length) {
          // Add default examples for any missing variables
          const completeExamples = [...filteredExamples];

          while (completeExamples.length < bodyVariables.length) {
            completeExamples.push(`Example ${completeExamples.length + 1}`);
          }

          bodyComponent.example = {
            body_text: [completeExamples],
          };
        } else if (filteredExamples.length > 0) {
          // All examples are provided
          bodyComponent.example = {
            body_text: [filteredExamples],
          };
        }

        console.log('[getTemplatePayload] Added examples for variables:', bodyComponent.example);
      }
    }

    payload.components.push(bodyComponent);

    // Add footer component if it exists
    if (templateFooter || template.category === 'AUTHENTICATION') {
      const footerComponent = {
        type: 'FOOTER',
      };

      // For authentication templates, we need code_expiration_minutes
      if (template.category === 'AUTHENTICATION' && showExpiryTime) {
        // Set code_expiration_minutes only if expiry time is enabled
        footerComponent.code_expiration_minutes = codeExp || 10;
      } else if (template.category !== 'AUTHENTICATION') {
        footerComponent.text = templateFooter;
      }

      // Only add footer component if it has content
      if ((template.category === 'AUTHENTICATION' && showExpiryTime) ||
        (template.category !== 'AUTHENTICATION' && templateFooter)) {
        payload.components.push(footerComponent);
      }
    }

    // Add buttons component if it exists
    if (templateButtons.length > 0) {
      const buttonsComponent = {
        type: 'BUTTONS',
        buttons: [],
      };

      templateButtons.forEach((button) => {
        if (button.type === 'QUICK_REPLY') {
          buttonsComponent.buttons.push({
            type: 'QUICK_REPLY',
            text: button.text,
          });
        } else if (button.type === 'URL') {
          buttonsComponent.buttons.push({
            type: 'URL',
            text: button.text,
            url: button.url,
          });
        } else if (button.type === 'PHONE_NUMBER') {
          buttonsComponent.buttons.push({
            type: 'PHONE_NUMBER',
            text: button.text,
            phone_number: button.countryCode ? `${button.countryCode}${button.phoneNumber}` : button.phoneNumber,
          });
        }
      });

      payload.components.push(buttonsComponent);
    }

    // For authentication templates, add an OTP button
    if (template.category === 'AUTHENTICATION') {
      // Check if we already have a buttons component
      let buttonsComponent = payload.components.find((c) => c.type === 'BUTTONS');

      if (!buttonsComponent) {
        buttonsComponent = {
          type: 'BUTTONS',
          buttons: [],
        };
        payload.components.push(buttonsComponent);
      }

      // Add OTP button if not already present
      const hasOtpButton = buttonsComponent.buttons.some((b) => b.type === 'OTP');
      if (!hasOtpButton) {
        buttonsComponent.buttons.push({
          type: 'OTP',
          otp_type: 'COPY_CODE'
          // The text field will be removed by the server for OTP buttons
          // so we don't include it in the payload
        });
      }
    }

    return payload;
  }, [
    template,
    templateHeader,
    templateBody,
    templateFooter,
    templateButtons,
    bodyVariables,
    bodyVariableEntries,
    codeExp,
    securityRecommendation,
    showExpiryTime,
  ]);

  return {
    template,
    templateHeader,
    templateBody,
    templateFooter,
    templateButtons,
    bodyVariables,
    bodyVariableEntries,
    codeExp,
    validityPeriod,
    bodyTextareaRef,
    isUploading,
    uploadError,
    securityRecommendation,
    setSecurityRecommendation,
    showExpiryTime,
    setShowExpiryTime,
    handleTemplateChange,
    handleBodyChange,
    handleMediaUpload,
    setTemplateHeader,
    setTemplateFooter,
    setCodeExp,
    setValidityPeriod,
    handleAddVariable,
    handleRemoveVariable,
    handleVariableEntryChange,
    handleButtonChange,
    handleAddButton,
    handleRemoveButton,
    handleTextFormat,
    validateTemplate,
    getTemplatePayload,
  };
};

export default useWhatsAppTemplate;
